<template>
  <c-loading-overlay
    :loading="loadingSubscriptionData"
  />
  <c-container
    size="small"
    class="plans-view"
    padding-size="none"
  >
    <div :style="{ padding: '2rem'}">
      <h2>
        Plans
      </h2>
      <p :style="{ color: $colors.grey.base }">
        Select a plan that suits your needs. Cancel anytime, no questions asked.
      </p>
      <c-divider size="small" />
    </div>
    <c-container
      size="small"
      padding-size="small"
    >
      <c-alert
        v-if="subscriptionPastDue"
        icon="warning"
        color="warning"
        title="Your subscription payment is past due. Please update your payment details to continue uninterrupted access."
        :emit-action="{ title: 'Update Payment Details'}"
        @alert-click="paddleUpdatePaymentMethod()"
      />
      <c-alert
        v-if="cancelled"
        icon="warning"
        color="warning"
        title="Your subscription has been cancelled. You will continue to have access until the end of the period, after which your subscription will be deactivated."
        :emit-action="{ title: 'Reactivate subscription'}"
        @alert-click="paddleReactivate()"
      />
      <h3>
        Your plan
      </h3>
      <div :style="{ display: 'flex' }">
        <c-card
          padding
          :style="{ flex: '1 1 auto' }"
        >
          <div :style="{ display: 'flex' }">
            <div :style="{ display: 'flex', flexWrap: 'wrap' }">
              <div :style="{ marginBottom: '2rem', marginRight: '2rem' }">
                <img :src="currentPlan.icon.src">
                <div>
                  <c-tag title="Current plan" />
                </div>
                <div :style="{ fontSize: $fontSizes[7], fontWeight: 'bold', margin: '1rem 0 .6rem' }">
                  {{ currentPlan.title }}
                </div>
                <div>
                  <template v-if="currentPlan.id === plans[0].id">
                    $0 / month
                  </template>
                  <template v-else>
                    {{ currentPlan.price }} / month
                  </template>
                </div>
                <div
                  v-if="!subscriptionPastDue && renewsAt !== ''"
                  :style="{padding: '1rem 0 0 0'}"
                >
                  <div v-if="!cancelled && !isTrialing">
                    <span :style="{fontWeight: '500'}">Renews at: </span>
                    <span>{{ renewsAt }}</span>
                  </div>
                  <div v-else-if="cancelled">
                    <span :style="{fontWeight: '500'}">Ends at: </span>
                    <span>{{ renewsAt }}</span>
                  </div>
                  <div v-else-if="isTrialing">
                    <span :style="{fontWeight: '500'}">Trial ends at: </span>
                    <span>{{ renewsAt }}</span>
                  </div>
                </div>
              </div>
              <div :style="{ flex: '1 1 30rem' }">
                <div>
                  <div :style="{ fontWeight: '500'}">
                    Includes:
                  </div>
                  <ul
                    v-for="(benefit, index) in currentPlan.benefits"
                    :key="index"
                    :style="{ color: $colors.grey.darken1, marginBlockStart: '.2rem', paddingInlineStart: '2rem', marginBlockEnd: 0 }"
                  >
                    <li>
                      {{ benefit.text }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </c-card>
        <div :style="{ marginLeft: '2rem' }">
          <c-card
            padding
            :style="{ flex: '0 0 3rem' }"
          >
            <h4 :style="{ marginTop: 0 }">
              Storage
            </h4>
            <storage-hint />
            <c-button
              v-if="nextPlan === null"
              outlined
              color="primary"
              href="https://tally.so/r/w2aPNA"
              target="_blank"
            >
              Contact us
            </c-button>
            <c-button
              v-else
              outlined
              color="primary"
              @click="handleCTAClick(nextPlan)"
            >
              {{ nextPlan.cta.text }}
            </c-button>
          </c-card>
        </div>
      </div>
    </c-container>
    <div
      v-if="currentPlan.id !== plans[0].id"
      :style="{ padding: '2rem 2rem 2rem' }"
    >
      <h3>Billing</h3>
      <div :style="{ marginBottom: '1rem' }">
        <c-button
          outlined
          @click="paddleUpdatePaymentMethod"
        >
          Change payment method
        </c-button>
      </div>
      <div>
        <c-button
          outlined
          @click="paddleCancel"
        >
          Cancel subscription
        </c-button>
      </div>
    </div>
    <c-container padding-size="small">
      <h3 :style="{ marginBottom: '-2.3rem' }">
        All plans
      </h3>
    </c-container>
    <div class="_pricing-table">
      <pricing-card
        v-for="(plan, i) in plans"
        :key="i"
        class="_card"
        :plan="plan"
        size="small"
        @cta-click="handleCTAClick"
      />
    </div>
  </c-container>
</template>

<script>
import CAlert from 'shared/components/CAlert.vue';
import CButton from 'shared/components/CButton.vue';
import CCard from 'shared/components/CCard.vue';
import CContainer from 'shared/components/CContainer.vue';
import CDivider from 'shared/components/CDivider.vue';
import CLoadingOverlay from 'shared/components/CLoadingOverlay.vue';
import CTag from 'shared/components/CTag.vue';
import PricingCard from 'shared/components/PricingCard.vue';
import StorageHint from 'shared/components/StorageHint.vue';
import { BETA_PHASE_MANAGER, paddleSubscriptionActions, USER_SERVICE } from 'shared/constants';
import { httpsCallable } from 'shared/services/firebase';
import reportHandledError from 'shared/services/report-error';
import useSubscription from 'shared/services/subscription';
import { inject, ref } from 'vue';

export default {
  name: 'PlansView',
  components: { CAlert, CTag, CCard, StorageHint, CDivider, CLoadingOverlay, CContainer, PricingCard, CButton },
  computed: {
    nextPlan() {
      if (this.currentPlan.id === this.plans[0].id) {
        return this.plans[1];
      }
      return null;
    },
  },
  methods: {
    setLoadingSubscriptionData() {
      this.loadingSubscriptionData = true;
      this.loadingTimeout = setTimeout(() => {
        if (this.loadingSubscriptionData === true) {
          this.loadingTimeout = null;
          this.loadingSubscriptionData = false;
          this.$eventBus.emit('show-toast', {
            message: 'Unable to complete the operation.',
            type: 'error',
          });
        }
      }, 60000);
    },
    handleCTAClick(plan) {
      if (this.isInBeta) {
        window.open('https://tally.so/r/w2aPNA', '_blank');
        return;
      }

      if (plan.cta.action === paddleSubscriptionActions.cancel) {
        this.paddleCancel();
        return;
      }

      if (plan.cta.action === paddleSubscriptionActions.checkout) {
        this.paddleCheckout(plan.cta.priceId);
        return;
      }

      if (plan.cta.action === paddleSubscriptionActions.reactivate) {
        this.paddleReactivate();
      }
    },
    async paddleCheckout(priceId) {
      this.paddle?.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customData: { notixUserId: this.currentUser.uid, checkoutType: 'open' },
      });
    },
    async paddleCancel() {
      this.setLoadingSubscriptionData();
      try {
        await httpsCallable(this.$functions, 'cancelPaddleSubscription')();
      } catch (e) {
        this.handleSubscriptionActionError();
      }
    },
    async paddleReactivate() {
      this.setLoadingSubscriptionData();
      try {
        await httpsCallable(this.$functions, 'reactivatePaddleSubscription')();
      } catch (e) {
        this.handleSubscriptionActionError();
      }
    },
    async paddleUpdatePaymentMethod() {
      this.loadingSubscriptionData = true;
      try {
        const res = await httpsCallable(this.$functions, 'transactionToUpdatePaymentMethod')();
        this.loadingSubscriptionData = false;
        if (res?.data) {
          this.paddle?.Checkout.open({
            transactionId: res.data.transactionId,
            customer: { id: res.data.paddleCustomerId },
            customData: { notixUserId: this.currentUser.uid, checkoutType: 'update' },
          });
        }
      } catch (e) {
        this.loadingSubscriptionData = false;
        this.$eventBus.emit('show-toast', {
          message: 'Unable to complete the operation. Please try again.',
          type: 'error',
        });
      }
    },
    handleSubscriptionActionError() {
      if (this.loadingTimeout !== null) {
        clearTimeout(this.loadingTimeout);
        this.loadingTimeout = null;
      }
      this.loadingSubscriptionData = false;
      this.$eventBus.emit('show-toast', {
        message: 'Unable to complete the operation. Please try again.',
        type: 'error',
      });
    },
  },
  watch: {
    loadingSubscriptionData() {
      if (this.loadingSubscriptionData === false && this.loadingTimeout !== null) {
        clearTimeout(this.loadingTimeout);
        this.loadingTimeout = null;
        this.$eventBus.emit('show-toast', {
          message: 'Operation completed successfully.',
          type: 'success',
        });
      }
    },
  },
  mounted() {
    const paddleEnvironment = import.meta.env.DEV ? 'sandbox' : 'production';
    console.log(window.Paddle);
    if (window.Paddle) {
      window.Paddle.Environment.set(paddleEnvironment);
      window.Paddle.Initialize({
        token: import.meta.env.VITE_PADDLE_TOKEN_CLIENT_SIDE,
        eventCallback: (res) => {
          if (res.name === 'checkout.completed' && res.data.custom_data.checkout_type === 'open') {
            this.setLoadingSubscriptionData();
          }
        },
      });
      this.paddle = window.Paddle;
    } else {
      // Billing is broken for this user if this happens, and nothing else
      // would tell us.
      reportHandledError(new Error('Paddle initialisation failed'), { where: 'plans view' });
    }
  },
  setup() {
    const paddle = ref();
    const loadingTimeout = ref(null);
    const userService = inject(USER_SERVICE);
    const betaPhaseManager = inject(BETA_PHASE_MANAGER);
    const { plans, currentPlan, renewsAt, cancelled, isTrialing, subscriptionPastDue, loadingSubscriptionData } = useSubscription(userService.currentUser, betaPhaseManager);
    return {
      paddle,
      isTrialing,
      cancelled,
      currentUser: userService.currentUser,
      userLoading: userService.loading,
      plans,
      currentPlan,
      renewsAt,
      loadingSubscriptionData,
      subscriptionPastDue,
      loadingTimeout,
      isInBeta: betaPhaseManager.isInBeta,
    };
  },
};
</script>

<style
  scoped
  type="text/scss"
  lang="scss"
>
@import "shared/assets/scss/box-shadow";

.plans-view {
  ._pricing-table {
    padding: 2rem 2rem 4rem;
    overflow-x: auto;
    display: flex;

    ._card {
      flex: 1 0 230px;

      &:not(:last-of-type) {
        margin-right: 2rem;
      }
    }
  }

  @media only screen and (max-width: 1100px) {
    ._pricing-table {
      grid-template-columns: 1fr;
      gap: 0;
      row-gap: 5rem;
    }
  }
}
</style>
